<template>
  <div class="hashtags-editor">

    <!-- =====================================================
         INITIAL SKELETON
    ====================================================== -->

    <div
      v-if="isInitialLoading && !selectedHashtags.length"
      class="skeleton-hashtags"
    >
      <div
        v-for="n in 5"
        :key="n"
        class="skeleton-hashtag shimmer"
      ></div>
    </div>


    <!-- =====================================================
         SELECTED TAGS
    ====================================================== -->

    <div
      v-if="selectedHashtags.length"
      class="selected-section"
    >

      <div class="selected-header">

        <div>
          <div class="selected-eyebrow">
            Выбрано
          </div>

          <div class="selected-title">
            Ваши профессиональные скиллы
          </div>
        </div>


        <div class="selected-count">
          {{ selectedHashtags.length }}
        </div>

      </div>


      <div class="selected-hashtags">

        <button
          v-for="hashtag in selectedHashtags"
          :key="hashtag.id"
          type="button"
          class="selected-hashtag"
          @click="removeHashtag(hashtag.id)"
        >

          <span class="hash-symbol">
            #
          </span>

          <span class="selected-name">
            {{ hashtag.name }}
          </span>


          <span class="remove-icon">

            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 5L11 11"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />

              <path
                d="M11 5L5 11"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>

          </span>

        </button>

      </div>

    </div>


    <!-- =====================================================
         EMPTY SELECTED STATE
    ====================================================== -->

    <div
      v-else-if="!isInitialLoading"
      class="empty-selected"
    >

      <div class="empty-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M9.5 4L7.5 20"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M16.5 4L14.5 20"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M4 9H20"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M3 15H19"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </div>


      <div>
        <div class="empty-title">
          Добавьте свои первые скиллы
        </div>

        <div class="empty-description">
          Например: Swift, Product Design, Python или Project Management.
        </div>
      </div>

    </div>


    <!-- =====================================================
         SEARCH
    ====================================================== -->

    <div class="search-section">

      <div class="search-heading">

        <div class="search-heading-icon">
          <svg
            viewBox="0 0 20 20"
            fill="none"
          >
            <circle
              cx="8.5"
              cy="8.5"
              r="5"
              stroke="currentColor"
              stroke-width="1.5"
            />

            <path
              d="M12.3 12.3L16 16"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </div>


        <div>
          <div class="search-title">
            Добавить скилл
          </div>

          <div class="search-subtitle">
            Найдите существующий тег или создайте новый
          </div>
        </div>

      </div>


      <div class="search-bar-container">

        <div
          class="search-bar"
          :class="{
            focused: isSearchFocused
          }"
        >

          <svg
            class="search-icon"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="8.5"
              cy="8.5"
              r="5"
              stroke="currentColor"
              stroke-width="1.5"
            />

            <path
              d="M12.3 12.3L16 16"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>


          <input
            v-model="searchQuery"
            type="text"
            placeholder="Например, Swift или Product Design"
            class="search-input"
            autocomplete="off"
            spellcheck="false"
            @input="searchHashtags"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
            @keydown.enter.prevent="handleEnter"
          />


          <button
            v-if="searchQuery"
            type="button"
            class="clear-search"
            @click="clearSearch"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M5 5L11 11"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />

              <path
                d="M11 5L5 11"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>

        </div>


        <button
          v-if="canCreateHashtag"
          type="button"
          class="create-button"
          :disabled="isCreating"
          @click="addHashtag"
        >

          <span
            v-if="isCreating"
            class="button-spinner"
          ></span>

          <template v-else>
            <span class="create-plus">
              +
            </span>

            Создать
          </template>

        </button>

      </div>


      <!-- =====================================================
           SEARCH LOADING
      ====================================================== -->

      <div
        v-if="isLoading"
        class="search-loading"
      >
        <span class="small-spinner"></span>

        <span>
          Ищем подходящие скиллы…
        </span>
      </div>


      <!-- =====================================================
           SEARCH RESULTS
      ====================================================== -->

      <div
        v-else-if="
          searchQuery.trim().length > 0
          && hashtags.length > 0
        "
        class="results-section"
      >

        <div class="results-label">
          Результаты
        </div>


        <div class="hashtags-list">

          <button
            v-for="hashtag in hashtags"
            :key="hashtag.id"
            type="button"
            class="hashtag"
            :class="{
              selected:
                isSelected(hashtag.id)
            }"
            @click="toggleHashtag(hashtag)"
          >

            <span class="result-hash">
              #
            </span>

            {{ hashtag.name }}


            <span
              v-if="isSelected(hashtag.id)"
              class="result-check"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 8.2L6.8 11L12 5.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>

          </button>

        </div>

      </div>


      <!-- =====================================================
           NO RESULTS
      ====================================================== -->

      <div
        v-else-if="
          searchQuery.trim().length > 0
          && !isLoading
          && hashtags.length === 0
        "
        class="no-results"
      >

        <div class="no-results-icon">
          #
        </div>


        <div>

          <div class="no-results-title">
            Такого скилла ещё нет
          </div>

          <div class="no-results-description">
            Можно создать
            <strong>#{{ searchQuery.trim() }}</strong>
            и добавить его в профиль.
          </div>

        </div>

      </div>

    </div>

  </div>
</template>


<script setup>

import {
  ref,
  watch,
  onMounted
} from "vue";

import HashtagService
  from "../api/HashtagService.js";


const props = defineProps({

  initialHashtags: {
    type: Array,
    default: () => []
  }

});


const emit = defineEmits([
  "save-hashtags"
]);


/* ============================================================
   STATE
============================================================ */

const searchQuery =
  ref("");

const hashtags =
  ref([]);

const selectedHashtags =
  ref([]);


const isLoading =
  ref(false);

const isCreating =
  ref(false);

const isInitialLoading =
  ref(true);

const isSearchFocused =
  ref(false);


let searchRequestId =
  0;


/* ============================================================
   INITIAL TAGS
============================================================ */

watch(
  () => props.initialHashtags,

  value => {

    selectedHashtags.value =
      normalizeInitialHashtags(
        value
      );

  },

  {
    immediate: true,
    deep: true
  }
);


onMounted(() => {
  isInitialLoading.value =
    false;
});


/* ============================================================
   SEND CHANGES TO PARENT
============================================================ */

watch(
  selectedHashtags,

  value => {

    emit(
      "save-hashtags",
      [...value]
    );

  },

  {
    deep: true
  }
);


/* ============================================================
   NORMALIZE
============================================================ */

function normalizeInitialHashtags(
  value = []
) {

  return value.map(tag => {

    if (
      typeof tag === "string"
    ) {

      return {
        id: tag,
        name: tag
      };

    }


    return {
      ...tag,
      id:
        tag.id
        ?? tag._id
        ?? tag.name,

      name:
        tag.name
        ?? tag.hashtag
        ?? String(tag.id ?? "")
    };

  });

}


/* ============================================================
   SEARCH
============================================================ */

async function searchHashtags() {

  const query =
    searchQuery.value.trim();


  if (!query) {

    hashtags.value = [];

    isLoading.value = false;

    return;
  }


  const requestId =
    ++searchRequestId;


  isLoading.value =
    true;


  try {

    const result =
      await HashtagService.searchHashtags(
        query
      );


    /*
     * Если пользователь уже ввёл
     * новый запрос — игнорируем
     * старый ответ.
     */

    if (
      requestId
      !== searchRequestId
    ) {
      return;
    }


    hashtags.value =
      Array.isArray(result)
        ? result
        : [];


  } catch (error) {

    console.error(
      "❌ Ошибка поиска хэштегов:",
      error
    );


    if (
      requestId
      === searchRequestId
    ) {
      hashtags.value = [];
    }


  } finally {

    if (
      requestId
      === searchRequestId
    ) {
      isLoading.value =
        false;
    }

  }

}


/* ============================================================
   SELECT
============================================================ */

function isSelected(id) {

  return selectedHashtags.value
    .some(
      tag =>
        tag.id === id
    );

}


function toggleHashtag(hashtag) {

  if (
    isSelected(hashtag.id)
  ) {

    removeHashtag(
      hashtag.id
    );

    return;
  }


  selectedHashtags.value.push({

    id:
      hashtag.id,

    name:
      hashtag.name

  });


  clearSearch();

}


/* ============================================================
   REMOVE
============================================================ */

function removeHashtag(id) {

  selectedHashtags.value =
    selectedHashtags.value.filter(
      tag =>
        tag.id !== id
    );

}


/* ============================================================
   CREATE
============================================================ */

const canCreateHashtag =
  ref(false);


watch(
  [
    searchQuery,
    hashtags,
    isLoading
  ],

  () => {

    const query =
      searchQuery.value.trim();


    canCreateHashtag.value =
      query.length > 0
      && !isLoading.value
      && hashtags.value.length === 0;

  },

  {
    deep: true
  }
);


async function addHashtag() {

  const name =
    searchQuery.value.trim();


  if (
    !name
    || isCreating.value
  ) {
    return;
  }


  isCreating.value =
    true;


  try {

    const newHashtag =
      await HashtagService.addHashtag(
        name
      );


    if (!newHashtag) {
      return;
    }


    if (
      !isSelected(
        newHashtag.id
      )
    ) {

      selectedHashtags.value.push({

        id:
          newHashtag.id,

        name:
          newHashtag.name

      });

    }


    clearSearch();


  } catch (error) {

    console.error(
      "❌ Ошибка создания хэштега:",
      error
    );


  } finally {

    isCreating.value =
      false;

  }

}


/* ============================================================
   ENTER
============================================================ */

function handleEnter() {

  if (
    hashtags.value.length
    === 1
  ) {

    toggleHashtag(
      hashtags.value[0]
    );

    return;
  }


  if (
    canCreateHashtag.value
  ) {
    addHashtag();
  }

}


/* ============================================================
   CLEAR
============================================================ */

function clearSearch() {

  searchRequestId += 1;

  searchQuery.value =
    "";

  hashtags.value =
    [];

  isLoading.value =
    false;

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.hashtags-editor {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 18px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   SELECTED
============================================================ */

.selected-section {
  padding-bottom: 18px;

  border-bottom:
    1px solid
    rgba(
      25,
      40,
      60,
      0.065
    );
}


.selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 15px;

  margin-bottom: 12px;
}


.selected-eyebrow {
  margin-bottom: 4px;

  color: #9a9fa7;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.06em;

  text-transform: uppercase;
}


.selected-title {
  color: #4b5058;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.selected-count {
  min-width: 27px;
  height: 27px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding:
    0
    8px;

  box-sizing: border-box;

  color: #6683aa;

  background:
    rgba(
      50,
      110,
      205,
      0.065
    );

  border:
    1px solid
    rgba(
      50,
      110,
      205,
      0.08
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


.selected-hashtags {
  display: flex;
  flex-wrap: wrap;

  gap: 7px;
}


.selected-hashtag {
  min-height: 31px;

  display: inline-flex;
  align-items: center;

  gap: 4px;

  padding:
    0
    7px
    0
    10px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3488ee,
      #496fe6
    );

  border: none;
  border-radius: 999px;

  box-shadow:
    0
    5px
    13px
    rgba(
      50,
      110,
      220,
      0.15
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;

  transition:
    transform
    0.18s ease,
    box-shadow
    0.18s ease;
}


.selected-hashtag:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0
    7px
    17px
    rgba(
      50,
      110,
      220,
      0.21
    );
}


.hash-symbol {
  opacity: 0.65;
}


.remove-icon {
  width: 19px;
  height: 19px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  margin-left: 2px;

  color:
    rgba(
      255,
      255,
      255,
      0.78
    );

  background:
    rgba(
      255,
      255,
      255,
      0.12
    );

  border-radius: 50%;
}


.remove-icon svg {
  width: 10px;
  height: 10px;
}


/* ============================================================
   EMPTY
============================================================ */

.empty-selected {
  display: flex;
  align-items: center;

  gap: 11px;

  padding: 14px;

  background:
    rgba(
      247,
      249,
      252,
      0.8
    );

  border:
    1px dashed
    rgba(
      35,
      50,
      70,
      0.1
    );

  border-radius: 14px;
}


.empty-icon {
  width: 35px;
  height: 35px;

  flex:
    0
    0
    35px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #5c82bd;

  background:
    rgba(
      65,
      115,
      195,
      0.07
    );

  border-radius: 10px;
}


.empty-icon svg {
  width: 18px;
  height: 18px;
}


.empty-title {
  color: #565c65;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.empty-description {
  margin-top: 3px;

  color: #9ba0a8;

  font-size: 8px;

  line-height: 1.4;
}


/* ============================================================
   SEARCH
============================================================ */

.search-section {
  display: flex;
  flex-direction: column;
}


.search-heading {
  display: flex;
  align-items: center;

  gap: 9px;

  margin-bottom: 11px;
}


.search-heading-icon {
  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #6c87b3;

  background:
    rgba(
      60,
      110,
      190,
      0.055
    );

  border-radius: 9px;
}


.search-heading-icon svg {
  width: 15px;
  height: 15px;
}


.search-title {
  color: #525860;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.search-subtitle {
  margin-top: 2px;

  color: #a0a4ac;

  font-size: 7px;
}


.search-bar-container {
  display: flex;
  align-items: center;

  gap: 9px;
}


.search-bar {
  min-width: 0;

  min-height: 45px;

  flex: 1;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  padding:
    0
    10px
    0
    13px;

  background: #f6f8fa;

  border:
    1px solid
    #e2e6eb;

  border-radius: 13px;

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.search-bar.focused {
  background: #ffffff;

  border-color:
    #5c8ff7;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      92,
      143,
      247,
      0.10
    );
}


.search-icon {
  width: 16px;
  height: 16px;

  flex:
    0
    0
    16px;

  margin-right: 9px;

  color: #9298a2;
}


.search-input {
  min-width: 0;

  flex: 1;

  padding: 0;

  color: #30353d;

  background: transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


.search-input::placeholder {
  color: #a9aeb6;
}


.clear-search {
  width: 28px;
  height: 28px;

  flex:
    0
    0
    28px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #9da2aa;

  background: transparent;

  border: none;
  border-radius: 8px;

  cursor: pointer;
}


.clear-search:hover {
  color: #616770;

  background:
    rgba(
      30,
      45,
      65,
      0.055
    );
}


.clear-search svg {
  width: 12px;
  height: 12px;
}


/* ============================================================
   CREATE
============================================================ */

.create-button {
  min-height: 45px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  padding:
    0
    14px;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4968e6
    );

  border: none;
  border-radius: 12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  box-shadow:
    0
    6px
    15px
    rgba(
      40,
      110,
      220,
      0.17
    );

  cursor: pointer;

  transition:
    transform
    0.18s ease,
    box-shadow
    0.18s ease;
}


.create-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    9px
    19px
    rgba(
      40,
      110,
      220,
      0.23
    );
}


.create-button:disabled {
  opacity: 0.6;

  cursor: default;
}


.create-plus {
  font-family: Arial, sans-serif;

  font-size: 15px;

  font-weight: 300;

  line-height: 1;
}


/* ============================================================
   RESULTS
============================================================ */

.results-section {
  margin-top: 14px;
}


.results-label {
  margin-bottom: 8px;

  color: #9da2aa;

  font-size: 7px;

  letter-spacing: 0.05em;

  text-transform: uppercase;
}


.hashtags-list {
  display: flex;
  flex-wrap: wrap;

  gap: 7px;
}


.hashtag {
  min-height: 31px;

  display: inline-flex;
  align-items: center;

  gap: 4px;

  padding:
    0
    10px;

  color: #59616c;

  background:
    #f4f6f9;

  border:
    1px solid
    rgba(
      30,
      45,
      65,
      0.065
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  cursor: pointer;

  transition:
    color
    0.18s ease,
    background-color
    0.18s ease,
    border-color
    0.18s ease,
    transform
    0.18s ease;
}


.hashtag:hover {
  color: #3375d3;

  background:
    rgba(
      40,
      135,
      247,
      0.065
    );

  border-color:
    rgba(
      40,
      135,
      247,
      0.12
    );

  transform:
    translateY(-1px);
}


.hashtag.selected {
  color: #2872d9;

  background:
    rgba(
      40,
      135,
      247,
      0.09
    );

  border-color:
    rgba(
      40,
      135,
      247,
      0.15
    );
}


.result-hash {
  opacity: 0.55;
}


.result-check {
  width: 16px;
  height: 16px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  margin-left: 2px;

  color: #3a9e69;

  background:
    rgba(
      50,
      175,
      105,
      0.08
    );

  border-radius: 50%;
}


.result-check svg {
  width: 10px;
  height: 10px;
}


/* ============================================================
   LOADING
============================================================ */

.search-loading {
  display: inline-flex;
  align-items: center;

  gap: 7px;

  margin-top: 12px;

  color: #959aa2;

  font-size: 8px;
}


.small-spinner,
.button-spinner {
  border:
    2px solid
    rgba(
      40,
      135,
      247,
      0.16
    );

  border-top-color:
    #2887f7;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}


.small-spinner {
  width: 14px;
  height: 14px;
}


.button-spinner {
  width: 13px;
  height: 13px;

  border-color:
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color: #ffffff;
}


@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   NO RESULTS
============================================================ */

.no-results {
  display: flex;
  align-items: center;

  gap: 10px;

  margin-top: 13px;

  padding: 12px;

  background:
    rgba(
      246,
      248,
      251,
      0.75
    );

  border:
    1px dashed
    rgba(
      30,
      45,
      65,
      0.09
    );

  border-radius: 12px;
}


.no-results-icon {
  width: 31px;
  height: 31px;

  flex:
    0
    0
    31px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #6284b9;

  background:
    rgba(
      65,
      115,
      195,
      0.065
    );

  border-radius: 9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;
}


.no-results-title {
  color: #666c75;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


.no-results-description {
  margin-top: 3px;

  color: #9ca1a9;

  font-size: 7px;

  line-height: 1.4;
}


.no-results-description strong {
  color: #6584b2;

  font-weight: 500;
}


/* ============================================================
   SKELETON
============================================================ */

.skeleton-hashtags {
  display: flex;
  flex-wrap: wrap;

  gap: 7px;
}


.skeleton-hashtag {
  position: relative;

  width: 82px;
  height: 31px;

  overflow: hidden;

  background: #eceef1;

  border-radius: 999px;
}


.shimmer::after {
  content: "";

  position: absolute;

  inset: 0;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(
        255,
        255,
        255,
        0.65
      ),
      transparent
    );

  transform:
    translateX(-100%);

  animation:
    shimmer
    1.4s
    infinite;
}


@keyframes shimmer {

  100% {
    transform:
      translateX(100%);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.selected-section {
  border-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.selected-title,
html[data-theme="dark"]
.search-title {
  color: #d5d8dd;
}


html[data-theme="dark"]
.empty-selected,
html[data-theme="dark"]
.no-results {
  background:
    rgba(
      255,
      255,
      255,
      0.025
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.075
    );
}


html[data-theme="dark"]
.empty-title,
html[data-theme="dark"]
.no-results-title {
  color: #c7cad0;
}


html[data-theme="dark"]
.empty-description,
html[data-theme="dark"]
.no-results-description {
  color: #7f848d;
}


html[data-theme="dark"]
.search-bar {
  background: #292b30;

  border-color: #3b3e45;
}


html[data-theme="dark"]
.search-bar.focused {
  background: #2e3036;

  border-color: #588bec;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      88,
      139,
      236,
      0.13
    );
}


html[data-theme="dark"]
.search-input {
  color: #f0f1f3;
}


html[data-theme="dark"]
.search-input::placeholder {
  color: #727780;
}


html[data-theme="dark"]
.hashtag {
  color: #adb2ba;

  background:
    rgba(
      255,
      255,
      255,
      0.04
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.hashtag:hover,
html[data-theme="dark"]
.hashtag.selected {
  color: #91bdff;

  background:
    rgba(
      40,
      135,
      247,
      0.11
    );

  border-color:
    rgba(
      80,
      150,
      245,
      0.15
    );
}


html[data-theme="dark"]
.skeleton-hashtag {
  background: #303238;
}


html[data-theme="dark"]
.shimmer::after {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(
        255,
        255,
        255,
        0.055
      ),
      transparent
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .search-bar-container {
    align-items: stretch;

    flex-direction: column;
  }


  .create-button {
    width: 100%;

    min-height: 41px;
  }


  .selected-hashtag,
  .hashtag {
    min-height: 30px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .selected-hashtag,
  .search-bar,
  .create-button,
  .hashtag {
    transition: none;
  }


  .small-spinner,
  .button-spinner,
  .shimmer::after {
    animation: none;
  }

}

</style>